import { isSameDay, startOfDay, endOfDay, areIntervalsOverlapping } from 'date-fns'
import { filter } from 'lodash'

import Api from 'services/Api'
import { Format, Parse } from 'services/Dates'
import { getDepartmentNames, getUserName, getTeamNames } from 'services/Utilities'
import { getCSV, hasAccessToThis, isPartialDate } from 'services/Utilities'
import { createErrorNotification } from 'store/actions/notifications'
import { store } from 'store/reducers'
import { currentLang, t } from 'translations'

export const timeOffStatuses = [
  { _id: 'IN_PROGRESS', name: t('manager.timeOffs.IN_PROGRESS') },
  { _id: 'APPROVED', name: t('manager.timeOffs.APPROVED') },
  { _id: 'REFUSED', name: t('manager.timeOffs.REFUSED') }
]

export async function getCommentsCount(ids, type) {
  const { count, success } = await Api(`/comments/count?resourceType=${type}&resourceIds=${ids.join('&resourceIds=')}`)

  return { count, success }
}

export async function getComments(ids, type) {
  const { result, success } = await Api(`/comments/comments?resourceType=${type}&resourceIds=${ids.join('&resourceIds=')}`)

  return { result, success }
}

export const getColor = status => {
  switch (status) {
    case 'APPROVED':
    case 'ACKNOWLEDGED':
    case 'ACKNOWLEDGE':
    case 'JUSTIFIED':
      return 'text-success'
    case 'LATENESS':
    case 'EARLYOUT':
    case 'UNJUSTIFIED':
    case 'CHANGES_REQUESTED':
      return 'text-warning'
    case 'ABSENCE':
    case 'CANCELLED':
    case 'REFUSED':
      return 'text-danger'
    default:
      return null
  }
}

export const generateDateText = (start, end, { partialOnlyTime } = {}) => {
  start = Parse(start)
  end = Parse(end)
  const sameDay = isSameDay(start, end)
  const partialDate = isPartialDate(start, end, Format)

  const startDate = Format(start, { outFormat: 'humanDate' })
  const startTime = Format(start, { outFormat: 'time' })
  const endDate = Format(end, { outFormat: 'humanDate' })
  const endTime = Format(end, { outFormat: 'time' })

  if (!partialDate) {
    if (!sameDay) return `${startDate} - ${endDate}`
    else return `${startDate}`
  } else {
    if (partialOnlyTime) return `${startTime} - ${endTime}`
    else if (!sameDay) return `${startDate} (${startTime}) - ${endDate} (${endTime})`
    else return `${startDate} (${startTime} - ${endTime})`
  }
}

export async function getAttendanceCSV({ filter, content }) {
  let { rangeStart, rangeEnd } = filter || {}

  if (rangeStart) rangeStart = Format(startOfDay(Parse(rangeStart, { inFormat: 'date' })), { outFormat: 'server' })
  if (rangeEnd) rangeEnd = Format(endOfDay(Parse(rangeEnd, { inFormat: 'date' })), { outFormat: 'server' })

  const processContent = content.map(attendance => {
    const statusId = t(`manager.users.${attendance.statusId}`)
    const archived = attendance.archived ? t(`TRUE`) : t(`FALSE`)
    const teams = getTeamNames(attendance.teams)

    const partialDate = isPartialDate(attendance.start, attendance.end)
    if (!partialDate) {
      attendance.start = Format(attendance.start, { outFormat: 'date' })
      attendance.end = Format(attendance.end, { outFormat: 'date' })
    }

    return { ...attendance, statusId, archived, teams }
  })

  let fields = [
    { value: 'dateRequestDateFormated', label: t('manager.attendances.csv.REQUEST_DATE') },
    { value: 'createdByName', label: t('manager.attendances.csv.CREATED_BY') },
    { value: 'dateValidateDateFormated', label: t('manager.attendances.csv.VALIDATION_DATE') },
    { value: 'validatedByName', label: t('manager.attendances.csv.VALIDATED_BY') },
    { value: 'leaveType', label: t('manager.attendances.csv.REQUEST_TYPE') },
    { value: 'comments', label: t('manager.attendances.csv.COMMENTS') },
    { value: 'reason', label: t('manager.attendances.csv.REASON') },
    { value: 'date', label: t('manager.attendances.csv.DATE') },
    { value: 'start', label: t('manager.attendances.csv.START') },
    { value: 'end', label: t('manager.attendances.csv.END') },
    { value: 'arrivalTime', label: t('manager.attendances.csv.ARRIVAL_TIME') },
    { value: 'departureTime', label: t('manager.attendances.csv.DEPARTURE_TIME') },
    { value: 'requestStatusId', label: t('manager.attendances.csv.REQUEST_STATUS') },
    { value: 'departments', label: t('manager.attendances.csv.DEPARTMENTS') },
    { value: 'teams', label: t('manager.attendances.csv.TEAMS') },
    { value: 'project', label: t('manager.attendances.csv.PROJECT') },
    { value: 'archived', label: t('manager.attendances.csv.ARCHIVED') }
  ]

  if (hasAccessToThis('manageAttendance')) {
    fields.unshift({ value: 'employeeNumber', label: t('manager.attendances.csv.EMPLOYEE_NUMBER') })
    fields.unshift({ value: 'employee', label: t('manager.attendances.csv.EMPLOYEE') })
  }

  getCSV(
    processContent,
    fields,
    t('manager.attendances.csv.ATTENDANCE_CSV_NAME', {
      date: `${Format(filter.rangeStart, { outFormat: 'date' })}-${Format(filter.rangeEnd, { outFormat: 'date' })}`
    })
  )
}

export async function getTimeOffCSV({ content, filter }) {
  const {
    settings: { enableOccupations }
  } = store.getState()

  content.forEach(timeOff => {
    timeOff.statusId = t(`manager.users.${timeOff.statusId}`)
    timeOff.archived = timeOff.archived ? t(`TRUE`) : t(`FALSE`)
    const partialDate = isPartialDate(timeOff.start, timeOff.end)
    if (!partialDate) {
      timeOff.start = Format(timeOff.start, { outFormat: 'date' })
      timeOff.end = Format(timeOff.end, { outFormat: 'date' })
    }
  })

  let fields = [
    { value: 'dateRequestDateFormated', label: t('manager.timeOffs.csv.REQUEST_DATE') },
    { value: 'createdByName', label: t('manager.timeOffs.csv.CREATED_BY') },
    { value: 'dateValidateDateFormated', label: t('manager.timeOffs.csv.VALIDATION_DATE') },
    { value: 'validatedByName', label: t('manager.timeOffs.csv.VALIDATED_BY') },
    { value: 'date', label: t('manager.timeOffs.csv.DATE') },
    { value: 'start', label: t('manager.timeOffs.csv.START') },
    { value: 'end', label: t('manager.timeOffs.csv.END') },
    { value: 'vacationType', label: t('manager.timeOffs.csv.VACATION_TYPE') },
    { value: 'comments', label: t('manager.timeOffs.csv.COMMENTS') },
    { value: 'requestStatusId', label: t('manager.timeOffs.csv.STATUS') },
    { value: 'archived', label: t('manager.attendances.csv.ARCHIVED') }
  ]

  if (enableOccupations)
    fields.unshift(
      { value: 'primaryOccupation', label: t('manager.timeOffs.csv.PRIMARY_OCCUPATION') },
      { value: 'secondaryOccupation', label: t('manager.timeOffs.csv.SECONDARY_OCCUPATION') }
    )

  if (hasAccessToThis('manageTimeOff')) {
    fields.unshift({ value: 'employeeNumber', label: t('manager.timeOffs.csv.EMPLOYEE_NUMBER') })
    fields.unshift({ value: 'employee', label: t('manager.timeOffs.csv.EMPLOYEE') })
  }

  getCSV(
    content,
    fields,
    t('manager.timeOffs.csv.TIMEOFF_CSV_NAME', {
      date: `${Format(filter.rangeStart, { outFormat: 'date' })}-${Format(filter.rangeEnd, { outFormat: 'date' })}`
    })
  )
}

export async function getAvailabilitiesCSV({ filter, sortBy }) {
  const {
    resources: { usersById }
  } = store.getState()

  const { result, success } = await Api(`/requests/availability`, { params: { ...filter, sort: sortBy } })
  if (!success) return store.dispatch(dispatch => dispatch(createErrorNotification()))

  // Parse the availabilities
  const data = result.map(availability => {
    const parsedAvailability = {
      fullName: getUserName(availability.userId),
      employeeNumber: getUserName(availability.userId, { employeeNumberOnly: true }),
      departments: getDepartmentNames(usersById[availability.userId]?.departments),
      status: t(`manager.availabilities.${availability.approvalStatus}`),
      archived: usersById[availability.userId]?.archived ? t(`TRUE`) : t(`FALSE`),
      dateValid: Format(availability.dateValid, { outFormat: 'date' }),
      name: availability.name,
      comment: availability.comment,
      notes: availability.notes,
      notSubmittedEnoughInAdvance: availability.notSubmittedEnoughInAdvance ? 'X' : '',
      ...availability.days.reduce((acc, day) => {
        // User has specific availabilities
        if (day.availabilities.length) {
          let partialAvailabilities = []
          day.availabilities.forEach(avail => {
            if (avail.type === 'UNAVAILABLE') {
              partialAvailabilities.push(`${t('manager.scheduleCapacity.csv.NOT_AVAILABLE_REPORT')} ${avail.start} - ${avail.end}`)
            } else if (avail.type === 'PREFERRED') {
              partialAvailabilities.push(`${t('manager.scheduleCapacity.csv.PREFERED_TIME')} ${avail.start} - ${avail.end}`)
            } else if (avail.type === 'AVAILABLE') {
              partialAvailabilities.push(`${t('manager.scheduleCapacity.csv.AVAILABLE_TIME')} ${avail.start} - ${avail.end}`)
            }
          })
          return { ...acc, [day.dayOfWeek]: partialAvailabilities.toString() }
          // User is not available all day
        } else if (day.notAvailable) {
          return { ...acc, [day.dayOfWeek]: t('manager.scheduleCapacity.csv.UNAVAILABLE') }
          // User is available all day
        } else {
          return { ...acc, [day.dayOfWeek]: t('manager.scheduleCapacity.csv.AVAILABLE_ALL_DAY') }
        }
      }, {})
    }
    if (availability.createdAt) parsedAvailability.createdAt = Format(availability.createdAt, { outFormat: 'dateTime' })
    if (availability.createdBy) parsedAvailability.createdBy = getUserName(availability.createdBy)
    if (availability.updatedAt) parsedAvailability.updatedAt = Format(availability.updatedAt, { outFormat: 'dateTime' })
    if (availability.updatedBy) parsedAvailability.updatedBy = getUserName(availability.updatedBy)
    if (availability.validatedAt) parsedAvailability.validatedAt = Format(availability.validatedAt, { outFormat: 'dateTime' })
    if (availability.validatedBy) parsedAvailability.validatedBy = getUserName(availability.validatedBy)
    if (availability.dateExpired) parsedAvailability.dateExpired = Format(availability.dateExpired, { outFormat: 'date' })

    return parsedAvailability
  })

  let fields = [
    { value: 'fullName', label: t('manager.availabilities.csv.FULLNAME') },
    { value: 'employeeNumber', label: t('manager.availabilities.csv.EMPLOYEENUMBER') },
    { value: 'status', label: t('manager.attendances.csv.STATUS') },
    { value: 'archived', label: t('manager.attendances.csv.ARCHIVED') },
    { value: 'dateValid', label: t('manager.availabilities.csv.DATEVALID') },
    { value: 'createdAt', label: t('manager.availabilities.csv.CREATIONDATE') },
    { value: 'createdBy', label: t('manager.availabilities.csv.CREATEDBY') },
    { value: 'updatedAt', label: t('manager.availabilities.csv.LASTUPDATED') },
    { value: 'updatedBy', label: t('manager.availabilities.csv.LASTUPDATEBY') },
    { value: 'validatedAt', label: t('manager.availabilities.csv.VALIDATEDDATE') },
    { value: 'validatedBy', label: t('manager.availabilities.csv.VALIDATEDBYID') },
    { value: 'name', label: t('manager.availabilities.csv.AVAILABILITY_NAME') },
    { value: 'comment', label: t('manager.availabilities.csv.COMMENT') },
    { value: 'notes', label: t('manager.availabilities.csv.NOTES') },
    { value: 'dateExpired', label: t('manager.availabilities.csv.DATEEXPIRED') },
    { value: 'notSubmittedEnoughInAdvance', label: t('manager.availabilities.csv.NOTSUBMITTEDENOUGHINADVANCE') },
    { value: '0', label: t(`components.dayOfWeek.0`) },
    { value: '1', label: t(`components.dayOfWeek.1`) },
    { value: '2', label: t(`components.dayOfWeek.2`) },
    { value: '3', label: t(`components.dayOfWeek.3`) },
    { value: '4', label: t(`components.dayOfWeek.4`) },
    { value: '5', label: t(`components.dayOfWeek.5`) },
    { value: '6', label: t(`components.dayOfWeek.6`) },
    { value: 'departments', label: t('manager.availabilities.csv.DEPARTMENTS') }
  ]

  getCSV(data, fields, t('manager.availabilities.csv.AVAILABILITIES_CSV_NAME', { date: Format(new Date(), { outFormat: 'date' }) }))
}

// Function to create the new comment in the DB
export const addComment = async (resourceType, resourceId, comment) => {
  // Build the comment
  let commentToSave = {
    resourceId,
    resourceType,
    text: comment
  }
  // Save the comment to the DB
  const { success, message } = await Api.post(`/comments/comments`, commentToSave)
  // Comment wasn't added successfully
  if (!success) throw new Error(message)
}

// Function to stop the submit cycle and display the error message
export const displayError = (actions, message) => {
  actions.setSubmitting(false)
  actions.setStatus({ type: 'danger', msg: message })
  return
}

// Function to filter the reasons not relevant to the attendance type selected
export const getAssignableReasons = (type = 'leaveReason', reasonId) => {
  const {
    resources: { attendanceReasons }
  } = store.getState()

  return attendanceReasons
    .filter(reason => {
      return reasonId === reason._id || reason.type === type
    })
    .map(reason => ({ ...reason, name: reason[`name${currentLang(true)}`] }))
}

export const requestTypeMap = {
  1: 'leaveReason',
  2: 'lateReason',
  3: 'earlyOutReason'
}

// Function to find the right default arrival/departure time to display
export const getArrivalDepartureDefaultTime = requestTypeId => {
  if (requestTypeId === 1) return {}
  if (requestTypeId === 2) return { arrivalTime: '08:00' }
  if (requestTypeId === 3) return { departureTime: '16:00' }
}

// Function to hierarchise a set of time off types
export const hierarchiseTimeOffTypes = (scheduleFilter = {}, { expanded }) => {
  const { vacationTypeId = [], positions = [] } = scheduleFilter
  let {
    resources: { timeOffTypes },
    settings: { showTimeOffInSchedule }
  } = store.getState()

  if (!showTimeOffInSchedule) timeOffTypes = timeOffTypes.filter(({ alwaysShownInSchedule }) => alwaysShownInSchedule)

  if (positions.length && !vacationTypeId.length) return []
  if (!timeOffTypes.length) return []

  const nameField = `name${currentLang(true)}`

  return [
    {
      name: t('manager.timeOffs.TIME_OFF'),
      id: 'timeOff',
      nonAssignable: true,
      eventHeight: 34,
      marginBottom: 0,
      isTimeOff: true,
      expanded,
      resourceType: 'timeOff',
      children: filterTimeOffType(
        timeOffTypes.map(({ [nameField]: name, _id: id }) => ({ name, id, resourceType: 'timeOff', isTimeOff: true })),
        vacationTypeId
      )
    }
  ]
}

function filterTimeOffType(types, vacationTypeId = []) {
  if (!vacationTypeId.length) return types
  return filter(types, type => vacationTypeId.includes(type.id))
}

export const checkOverlappingVacations = async (values, _id) => {
  const { success, vacations } = await Api.get(`/requests/vacation`, {
    params: { userId: values.userId, rangeStart: Format(values.start), rangeEnd: Format(values.end) }
  })

  if (!success) return store.dispatch(dispatch => dispatch(createErrorNotification()))
  const areVacationsOverlapping = vacations.some(otherTimeOff => {
    const validTimeOffs = ['APPROVED', 'IN_PROGRESS']
    const validTimeOff = validTimeOffs.includes(otherTimeOff.requestStatusId)
    if (otherTimeOff._id === _id) return false
    if (!validTimeOff) return false
    return areIntervalsOverlapping({ start: Parse(otherTimeOff.start), end: Parse(otherTimeOff.end) }, { start: Parse(values.start), end: Parse(values.end) })
  })
  return areVacationsOverlapping
}

export const fetchIssues = async (rangeStart, rangeEnd, userId, flags) => {
  const start = Format(startOfDay(Parse(rangeStart)), { outFormat: 'server' })
  const end = Format(endOfDay(Parse(rangeEnd)), { outFormat: 'server' })

  const { events: result, success } = await Api('/recipe/schedule/issue', {
    params: {
      filter: { rangeStart: start, rangeEnd: end, employeeId: userId },
      flags: flags.reduce((obj, key) => {
        obj[key] = true
        return obj
      }, {}),
      options: {
        checkUnapprovedAvailability: true,
        checkUnapprovedTimeOff: true
      }
    }
  })

  if (!success) return store.dispatch(dispatch => dispatch(createErrorNotification()))

  return result
}
